import React, { useEffect, useState } from 'react';
import { Card, Input, Button, Space, Typography, Spin, Alert, message, DatePicker, Select, Row, Col } from 'antd';
import { SaveOutlined, LockOutlined } from '@ant-design/icons';
import { opportunityApi } from '@/api/opportunityApi';
import { useAuth } from '@/auth/useAuth';
import dayjs from 'dayjs';

const { Text } = Typography;
const { TextArea } = Input;

interface IntakeDetailsFormProps {
  opportunityId: string;
  onComplete: () => void;
}

const IntakeDetailsForm: React.FC<IntakeDetailsFormProps> = ({ opportunityId, onComplete }) => {
  const { hasAnyRole } = useAuth();
  const canEdit = hasAnyRole(['OPPORTUNITY_FINDER', 'ADMIN', 'CEO']);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState('');
  const [agencyName, setAgencyName] = useState('');
  const [rfoNumber, setRfoNumber] = useState('');
  const [issueDate, setIssueDate] = useState<string | undefined>();
  const [dueDate, setDueDate] = useState<string | undefined>();
  const [dueTime, setDueTime] = useState('');
  const [awardDate, setAwardDate] = useState<string | undefined>();
  const [awardTime, setAwardTime] = useState('');
  const [submissionMethod, setSubmissionMethod] = useState('');
  const [procurementPortal, setProcurementPortal] = useState('');
  const [rfoLink, setRfoLink] = useState('');
  const [qaDeadline, setQaDeadline] = useState<string | undefined>();
  const [keyContacts, setKeyContacts] = useState('');
  const [mandatoryRequirements, setMandatoryRequirements] = useState('');
  const [extendedSubmissionDate, setExtendedSubmissionDate] = useState<string | undefined>();
  const [alreadyFilled, setAlreadyFilled] = useState(false);

  useEffect(() => {
    fetchDetails();
  }, [opportunityId]);

  const fetchDetails = async () => {
    setLoading(true);
    try {
      const opp = await opportunityApi.getById(opportunityId);
      setTitle(opp.title || '');
      setAgencyName(opp.agencyName || '');
      setRfoNumber(opp.rfoNumber || '');
      setIssueDate(opp.discoveryDate || undefined);
      setDueDate(opp.dueDate || undefined);
      setDueTime(opp.dueTime || '');
      setAwardDate(opp.awardDate || undefined);
      setAwardTime(opp.awardTime || '');
      setSubmissionMethod(opp.submissionMethod || '');
      setProcurementPortal(opp.sourcePortal || '');
      setRfoLink(opp.externalRef || '');
      setQaDeadline(opp.qaDeadline || undefined);
      setKeyContacts(opp.keyContacts || '');
      setMandatoryRequirements(opp.mandatoryRequirements || '');
      setExtendedSubmissionDate(opp.extendedSubmissionDate || undefined);
      setAlreadyFilled(Boolean(opp.agencyName && opp.rfoNumber));
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!agencyName.trim() || !rfoNumber.trim()) {
      message.warning('Agency Name and Solicitation Number are required');
      return;
    }
    setSaving(true);
    try {
      await opportunityApi.updateIntakeDetails(opportunityId, {
        agencyName: agencyName.trim(),
        rfoNumber: rfoNumber.trim(),
        discoveryDate: issueDate || undefined,
        dueDate: dueDate || undefined,
        dueTime: dueTime.trim() || undefined,
        submissionMethod: submissionMethod || undefined,
        sourcePortal: procurementPortal.trim() || undefined,
        externalRef: rfoLink.trim() || undefined,
        qaDeadline: qaDeadline || undefined,
        keyContacts: keyContacts.trim() || undefined,
        mandatoryRequirements: mandatoryRequirements.trim() || undefined,
        awardDate: awardDate || undefined,
        awardTime: awardTime.trim() || undefined,
        extendedSubmissionDate: extendedSubmissionDate || undefined,
      });
      message.success('Intake details saved');
      setAlreadyFilled(true);
      onComplete();
    } catch (err: any) {
      message.error(err.response?.data?.message || 'Failed to save intake details');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card size="small" style={{ marginTop: 12 }}>
        <Spin size="small" /> Loading intake details...
      </Card>
    );
  }

  return (
    <Card
      size="small"
      title={<Text strong style={{ fontSize: 14 }}>Phase A: Intake Details</Text>}
      style={{ marginTop: 12, borderColor: alreadyFilled ? 'var(--color-success)' : 'var(--color-warning)' }}
    >
      {!canEdit && (
        <Alert
          message={<span><LockOutlined /> Read-only — only the Opportunity Finder, Admin, or CEO can edit this section.</span>}
          type="info"
          showIcon={false}
          style={{ marginBottom: 12 }}
        />
      )}
      {!alreadyFilled && (
        <Alert
          message="Agency Name and Solicitation Number are required to advance to Phase B"
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Row gutter={[16, 12]}>
        {/* Title (read-only) */}
        <Col xs={24} md={12}>
          <Text strong style={{ display: 'block', marginBottom: 4 }}>Title</Text>
          <Input value={title} disabled />
        </Col>

        {/* Agency Name */}
        <Col xs={24} md={12}>
          <Text strong style={{ display: 'block', marginBottom: 4 }}>
            Agency Name <span style={{ color: 'var(--color-error)' }}>*</span>
          </Text>
          <Input
            value={agencyName}
            onChange={(e) => setAgencyName(e.target.value)}
            placeholder="e.g. Department of Defense"
            disabled={!canEdit}
          />
        </Col>

        {/* Solicitation Number */}
        <Col xs={24} md={12}>
          <Text strong style={{ display: 'block', marginBottom: 4 }}>
            Solicitation Number <span style={{ color: 'var(--color-error)' }}>*</span>
          </Text>
          <Input
            value={rfoNumber}
            onChange={(e) => setRfoNumber(e.target.value)}
            placeholder="e.g. W15P7T-24-R-0001"
            disabled={!canEdit}
          />
        </Col>

        {/* Issue Date */}
        <Col xs={24} md={12}>
          <Text strong style={{ display: 'block', marginBottom: 4 }}>Issue Date</Text>
          <DatePicker
            style={{ width: '100%' }}
            value={issueDate ? dayjs(issueDate) : null}
            onChange={(date) => setIssueDate(date ? date.format('YYYY-MM-DD') : undefined)}
            disabled={!canEdit}
          />
        </Col>

        {/* Due Date */}
        <Col xs={24} md={8}>
          <Text strong style={{ display: 'block', marginBottom: 4 }}>Due Date</Text>
          <DatePicker
            style={{ width: '100%' }}
            value={dueDate ? dayjs(dueDate) : null}
            onChange={(date) => setDueDate(date ? date.format('YYYY-MM-DD') : undefined)}
            disabled={!canEdit}
          />
        </Col>

        {/* Due Time */}
        <Col xs={24} md={4}>
          <Text strong style={{ display: 'block', marginBottom: 4 }}>Due Time</Text>
          <Input
            value={dueTime}
            onChange={(e) => setDueTime(e.target.value)}
            placeholder="e.g. 2:00 PM EST"
            disabled={!canEdit}
          />
        </Col>

        {/* Submission Method */}
        <Col xs={24} md={12}>
          <Text strong style={{ display: 'block', marginBottom: 4 }}>Submission Method</Text>
          <Select
            style={{ width: '100%' }}
            value={submissionMethod || undefined}
            onChange={setSubmissionMethod}
            placeholder="Select submission method"
            allowClear
            disabled={!canEdit}
          >
            <Select.Option value="Email">Email</Select.Option>
            <Select.Option value="Portal">Portal</Select.Option>
            <Select.Option value="Other">Other</Select.Option>
          </Select>
        </Col>

        {/* Procurement Portal */}
        <Col xs={24} md={12}>
          <Text strong style={{ display: 'block', marginBottom: 4 }}>Procurement Portal</Text>
          <Input
            value={procurementPortal}
            onChange={(e) => setProcurementPortal(e.target.value)}
            placeholder="e.g. SAM.gov, GovWin, FedConnect"
            disabled={!canEdit}
          />
        </Col>

        {/* RFO Link */}
        <Col xs={24} md={12}>
          <Text strong style={{ display: 'block', marginBottom: 4 }}>RFO Link</Text>
          <Input
            value={rfoLink}
            onChange={(e) => setRfoLink(e.target.value)}
            placeholder="https://..."
            disabled={!canEdit}
          />
        </Col>

        {/* Q&A Deadline */}
        <Col xs={24} md={12}>
          <Text strong style={{ display: 'block', marginBottom: 4 }}>Q&A Deadline</Text>
          <DatePicker
            style={{ width: '100%' }}
            value={qaDeadline ? dayjs(qaDeadline) : null}
            onChange={(date) => setQaDeadline(date ? date.format('YYYY-MM-DD') : undefined)}
            disabled={!canEdit}
          />
        </Col>

        {/* Award Date */}
        <Col xs={24} md={8}>
          <Text strong style={{ display: 'block', marginBottom: 4 }}>Award Date</Text>
          <DatePicker
            style={{ width: '100%' }}
            value={awardDate ? dayjs(awardDate) : null}
            onChange={(date) => setAwardDate(date ? date.format('YYYY-MM-DD') : undefined)}
            placeholder="Expected award date"
            disabled={!canEdit}
          />
        </Col>

        {/* Award Time */}
        <Col xs={24} md={4}>
          <Text strong style={{ display: 'block', marginBottom: 4 }}>Award Time</Text>
          <Input
            value={awardTime}
            onChange={(e) => setAwardTime(e.target.value)}
            placeholder="e.g. 3:00 PM EST"
            disabled={!canEdit}
          />
        </Col>

        {/* Extended Submission Date */}
        <Col xs={24} md={12}>
          <Text strong style={{ display: 'block', marginBottom: 4 }}>Extended Submission Date</Text>
          <DatePicker
            style={{ width: '100%' }}
            value={extendedSubmissionDate ? dayjs(extendedSubmissionDate) : null}
            onChange={(date) => setExtendedSubmissionDate(date ? date.format('YYYY-MM-DD') : undefined)}
            disabledDate={(current) => {
              if (!dueDate) return false;
              return current.isBefore(dayjs(dueDate), 'day');
            }}
            placeholder="Select extended submission date"
            disabled={!canEdit}
          />
        </Col>

        {/* Key Contacts */}
        <Col xs={24}>
          <Text strong style={{ display: 'block', marginBottom: 4 }}>Key Contacts</Text>
          <TextArea
            value={keyContacts}
            onChange={(e) => setKeyContacts(e.target.value)}
            placeholder="List key contacts (names, roles, emails, phone numbers)"
            rows={3}
            disabled={!canEdit}
          />
        </Col>

        {/* Mandatory Requirements / Comments */}
        <Col xs={24}>
          <Text strong style={{ display: 'block', marginBottom: 4 }}>Mandatory Requirements / Comments</Text>
          <TextArea
            value={mandatoryRequirements}
            onChange={(e) => setMandatoryRequirements(e.target.value)}
            placeholder="List mandatory requirements, certifications, or special conditions"
            rows={4}
            disabled={!canEdit}
          />
        </Col>

        {/* Save button */}
        <Col xs={24} style={{ textAlign: 'right' }}>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSave}
            loading={saving}
            disabled={!canEdit || !agencyName.trim() || !rfoNumber.trim()}
          >
            Save Intake Details
          </Button>
        </Col>
      </Row>
    </Card>
  );
};

export default IntakeDetailsForm;
